<template>
  <section class="capital-structure">
    <h2 class="tracking-normal font-semibold text-xl mb-4 lg_text-2xl">Capital Structure</h2>
    <div class="overflow-hidden border border-gray-200 rounded-lg">
      <table class="m-0 min-w-full">
        <tr v-for="option in options" :key="option.id" class="bg-white">
          <th class="text-gray-800 text-sm text-left font-medium cell-space">{{ option.name }}</th>
          <td class="text-center text-gray-700 text-sm border-0 cell-space whitespace-nowrap" :data-testid="option.id">
            {{ option.value }}<sup v-if="option.shares_outstanding_date">1</sup><sup v-if="option.financial_statement_date">2</sup>
          </td>
        </tr>
      </table>
    </div>
    <p class="text-gray-600 text-xs mt-4 md_text-right text-left">
      Financial Market Data powered by <a class="text-copper-700" href="http://quotemedia.com/">Quotemedia.com</a>. All rights reserved.
      <br class="sm_block hidden" />
      <a class="text-copper-700" href="https://quotemedia.com/legal/tos/">View the Terms of Use</a>. Data delayed 15 minutes unless
      indicated.<br />
      <span v-if="capitalStructure.shares_outstanding_date || capitalStructure.financial_statement_currency">
        <span v-if="capitalStructure.shares_outstanding_date">
          <sup>1</sup>&nbsp;As of {{ formatDateMed(capitalStructure.shares_outstanding_date) }},&nbsp;
        </span>
        <span v-if="capitalStructure.financial_statement_date">
          <sup>2</sup>&nbsp;As of {{ formatDateMed(capitalStructure.financial_statement_date) }}
        </span>
      </span>
    </p>
  </section>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue'
import { formatMoney, formatNumber } from '@/lib/companyPageHelpers'
import type { CompanyCapitalStructure } from '@/entities/companyCapitalStructure'
import { formatDateMed } from '@/lib/datetime-format-utils'

const props = defineProps({
  capitalStructure: {
    type: Object as PropType<CompanyCapitalStructure>,
    required: true,
  },
})

const options = ref([
  {
    id: 'market-cap',
    name: 'Market Cap',
    value:
      !props.capitalStructure.market_capitalization && props.capitalStructure.market_capitalization !== 0
        ? 'N/A'
        : `${props.capitalStructure.pricing_currency} $${formatMoney(props.capitalStructure.market_capitalization)}`,
  },
  {
    id: 'shares-outstanding',
    name: 'Shares Outstanding',
    value:
      !props.capitalStructure.basic_shares_outstanding && props.capitalStructure.basic_shares_outstanding !== 0
        ? 'N/A'
        : `${formatNumber(props.capitalStructure.basic_shares_outstanding)}`,
    shares_outstanding_date:
      (props.capitalStructure.basic_shares_outstanding || props.capitalStructure.basic_shares_outstanding === 0) ??
      props.capitalStructure.shares_outstanding_date,
  },
  {
    id: 'cash',
    name: 'Cash & Short Term Investments',
    value:
      !props.capitalStructure.cash && props.capitalStructure.cash !== 0
        ? 'N/A'
        : `${props.capitalStructure.financial_statement_currency} $${formatMoney(props.capitalStructure.cash)}`,
    financial_statement_date:
      (props.capitalStructure.cash || props.capitalStructure.cash === 0) ?? props.capitalStructure.financial_statement_date,
  },
  {
    id: 'debt',
    name: 'Debt',
    value:
      !props.capitalStructure.debt && props.capitalStructure.debt !== 0
        ? 'N/A'
        : `${props.capitalStructure.financial_statement_currency} $${formatMoney(props.capitalStructure.debt)}`,
    financial_statement_date:
      (props.capitalStructure.debt || props.capitalStructure.debt === 0) ?? props.capitalStructure.financial_statement_date,
  },
  {
    id: 'enterprise-value',
    name: 'Enterprise Value',
    value: !props.capitalStructure.enterprise_value
      ? 'N/A'
      : `${props.capitalStructure.pricing_currency} $${formatMoney(props.capitalStructure.enterprise_value)}`,
  },
])
</script>

<style lang="stylus" scoped>
.cell-space
  @apply px-6 py-4

tr:nth-child(n + 1)
  @apply border-b border-gray-200

tr:nth-child(even)
  @apply bg-gray-50

sup
  @apply font-bold
</style>
